// ─────────────────────────────────────────────────────────────────────────────
// V2Reports — the Reports group in the v2 design.
//   V2BusinessReport → /dashboard/reports          (/api/reports/business | /stock)
//   V2Financials     → /dashboard/financial-reports (/api/financials/*)
//   V2GSTReports     → /dashboard/gst-reports       (/api/gst-reports/*)
// Read-only. Every figure is the server's; the UI only arranges, labels and
// charts it. Charts are hand-built (CSS/SVG), single-axis, palette from the
// v2 data-viz set, with hover read-outs and a legend wherever >1 series.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Segmented, Skel, inr, inrCompact } from "./primitives";
import { PageHead, Stat } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const money = (v) => inr(v, { decimals: 2 });
const SERIES = { a: "#2a78d6", b: "#eb6834", c: "#1baf7a" };
const AGING = ["#f6c7a6", "#f09a5c", "#e0672f", "#a9441c"]; // one hue, light → dark = older

async function getJSON(path) {
  const r = await fetch(`${API_BASE}${path}`, { headers: auth() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.message || "Couldn't load this report");
  return d;
}
function useReport(path) {
  const [state, setState] = useState({ data: null, error: "", loading: true });
  useEffect(() => {
    if (!path) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    getJSON(path).then((data) => live && setState({ data, error: "", loading: false }))
      .catch((e) => live && setState({ data: null, error: e.message, loading: false }));
    return () => { live = false; };
  }, [path]);
  return state;
}

function downloadCSV(name, cols, rows) {
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(typeof c.get === "function" ? c.get(r) : r[c.get])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function printReport() {
  document.documentElement.setAttribute("data-v2-print", "report");
  const done = () => { document.documentElement.removeAttribute("data-v2-print"); window.removeEventListener("afterprint", done); };
  window.addEventListener("afterprint", done);
  window.print();
}

// ── Period: month / quarter / year (calendar or financial) ────────────────────
function usePeriod({ fy = false, initial = "month" } = {}) {
  const now = new Date();
  const align = (d, mode) => {
    if (mode === "month") return new Date(d.getFullYear(), d.getMonth(), 1);
    if (mode === "quarter") return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
    if (fy) return new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);
    return new Date(d.getFullYear(), 0, 1);
  };
  const [mode, setModeRaw] = useState(initial);
  const [start, setStart] = useState(() => align(now, initial));
  const span = mode === "month" ? 1 : mode === "quarter" ? 3 : 12;
  const end = new Date(start.getFullYear(), start.getMonth() + span, 0);
  const atLatest = new Date(start.getFullYear(), start.getMonth() + span, 1) > now;
  const short = (d) => MONTHS[d.getMonth()].slice(0, 3);
  const label = mode === "month" ? `${MONTHS[start.getMonth()]} ${start.getFullYear()}`
    : mode === "quarter" ? `${short(start)} – ${short(end)} ${end.getFullYear()}`
    : fy ? `FY ${start.getFullYear()}-${String(start.getFullYear() + 1).slice(2)}` : `${start.getFullYear()}`;
  return {
    mode, start, end, label, atLatest,
    setMode: (m) => { setModeRaw(m); setStart((s) => align(s, m)); },
    step: (dir) => setStart((s) => new Date(s.getFullYear(), s.getMonth() + dir * span, 1)),
  };
}
function PeriodPicker({ p, fy }) {
  return (
    <div className="v2rp-period">
      <Segmented ariaLabel="Period" value={p.mode} onChange={p.setMode}
        options={[{ value: "month", label: "Month" }, { value: "quarter", label: "Quarter" }, { value: "year", label: fy ? "FY" : "Year" }]} />
      <div className="v2-stepper v2-glass" role="group" aria-label="Choose period">
        <button type="button" aria-label="Previous period" onClick={() => p.step(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
        <span className="v2-stepper-label">{p.label}</span>
        <button type="button" aria-label="Next period" disabled={p.atLatest} onClick={() => p.step(1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
      </div>
    </div>
  );
}

// ── Building blocks ───────────────────────────────────────────────────────────
const Panel = ({ title, sub, action, children, className = "" }) => (
  <section className={`v2-card v2rp-panel ${className}`}>
    {(title || action) && <header className="v2rp-panel-head"><div><h3>{title}</h3>{sub && <span>{sub}</span>}</div>{action}</header>}
    {children}
  </section>
);
const Empty = ({ glyph = "chart", children }) => <div className="v2c-empty v2rp-empty"><Duo name={glyph} size={40} /><span>{children}</span></div>;
const Failed = ({ msg }) => <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {msg}</div>;

export function BarList({ rows, value = (r) => r.value, label = (r) => r.label, sub, fmt = inr, color = SERIES.a, empty }) {
  if (!rows?.length) return <Empty>{empty || "Nothing in this period."}</Empty>;
  const max = Math.max(...rows.map(value), 1);
  return (
    <ol className="v2rp-bars">
      {rows.map((r, i) => (
        <li key={i}>
          <div className="v2rp-bars-top"><span className="v2rp-bars-label">{label(r)}</span><b>{fmt(value(r))}</b></div>
          <div className="v2rp-bars-track"><span style={{ width: `${Math.max(1.5, (value(r) / max) * 100)}%`, background: color }} /></div>
          {sub && <span className="v2rp-bars-sub">{sub(r)}</span>}
        </li>))}
    </ol>
  );
}

export function AgingBar({ buckets, total }) {
  const sum = total ?? buckets.reduce((a, b) => a + b.value, 0);
  const [hi, setHi] = useState(-1);
  return (
    <div className="v2rp-aging">
      <div className="v2rp-aging-bar" role="img" aria-label={buckets.map((b) => `${b.label} ${inr(b.value)}`).join(", ")}>
        {sum > 0 ? buckets.map((b, i) => b.value > 0 && (
          <span key={i} style={{ flexGrow: b.value, background: AGING[i] }} className={hi === i ? "is-hi" : hi >= 0 ? "is-dim" : ""}
            onPointerEnter={() => setHi(i)} onPointerLeave={() => setHi(-1)} title={`${b.label}: ${inr(b.value)}`} />))
          : <span className="is-empty" style={{ flexGrow: 1 }} />}
      </div>
      <div className="v2rp-legend v2rp-legend--grid">
        {buckets.map((b, i) => (
          <div key={i} className={`v2rp-legend-item${hi === i ? " is-hi" : ""}`} onPointerEnter={() => setHi(i)} onPointerLeave={() => setHi(-1)}>
            <i style={{ background: AGING[i] }} /><span>{b.label}</span><b>{inr(b.value)}</b>
            <em>{sum > 0 ? `${Math.round((b.value / sum) * 100)}%` : "—"}</em>
          </div>))}
      </div>
    </div>
  );
}

const niceMax = (v) => { if (v <= 0) return 1; const e = 10 ** Math.floor(Math.log10(v)); const f = v / e; return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * e; };
// Paired monthly columns (two series, one axis) with a hover read-out.
export function MonthlyChart({ data, a = { key: "revenue", label: "Revenue" }, b = { key: "expenses", label: "Expenses" }, extra }) {
  const [hi, setHi] = useState(-1);
  const max = niceMax(Math.max(...data.map((d) => Math.max(d[a.key] || 0, d[b.key] || 0)), 0));
  const ticks = [1, 0.5, 0];
  const cur = data[hi];
  return (
    <div className="v2rp-chart">
      <div className="v2rp-legend">
        <span className="v2rp-legend-item"><i style={{ background: SERIES.a }} />{a.label}</span>
        <span className="v2rp-legend-item"><i style={{ background: SERIES.b }} />{b.label}</span>
      </div>
      <div className="v2rp-plot" onPointerLeave={() => setHi(-1)}>
        <div className="v2rp-axis">{ticks.map((t) => <span key={t} style={{ bottom: `${t * 100}%` }}>{inrCompact(max * t)}</span>)}</div>
        <div className="v2rp-grid">{ticks.map((t) => <i key={t} style={{ bottom: `${t * 100}%` }} />)}</div>
        <div className="v2rp-cols">
          {data.map((d, i) => (
            <div key={i} className={`v2rp-col${hi === i ? " is-hi" : ""}`} onPointerEnter={() => setHi(i)} onClick={() => setHi(i)}>
              <div className="v2rp-col-bars">
                <span style={{ height: `${((d[a.key] || 0) / max) * 100}%`, background: SERIES.a }} />
                <span style={{ height: `${((d[b.key] || 0) / max) * 100}%`, background: SERIES.b }} />
              </div>
              <span className="v2rp-col-label">{d.month}</span>
            </div>))}
        </div>
        {cur && (
          <div className="v2rp-tip" style={hi < data.length / 2
            ? { left: `${((hi + 1) / data.length) * 100}%`, transform: "translateX(6px)" }
            : { left: `${(hi / data.length) * 100}%`, transform: "translateX(calc(-100% - 6px))" }}>
            <strong>{cur.month}</strong>
            <span><i style={{ background: SERIES.a }} />{a.label}<b>{inr(cur[a.key])}</b></span>
            <span><i style={{ background: SERIES.b }} />{b.label}<b>{inr(cur[b.key])}</b></span>
            {extra?.(cur)}
          </div>)}
      </div>
    </div>
  );
}

// Statement lines (P&L, balance sheet, cash flow)
const Line = ({ label, value, level = 0, kind, note, neg }) => (
  <div className={`v2rp-line v2rp-line--l${level}${kind ? ` v2rp-line--${kind}` : ""}`}>
    <span>{label}{note && <em>{note}</em>}</span>
    <b>{value == null ? "" : `${neg || value < 0 ? "− " : ""}${money(Math.abs(value))}`}</b>
  </div>
);
const mapLines = (obj, level = 1) => Object.entries(obj || {}).filter(([, v]) => v).sort((x, y) => y[1] - x[1]).map(([k, v]) => <Line key={k} label={k || "Other"} value={v} level={level} />);

function Table({ cols, rows, foot, empty, max }) {
  if (!rows?.length) return <Empty glyph="gst">{empty || "No records in this period."}</Empty>;
  const shown = max ? rows.slice(0, max) : rows;
  return (
    <div className="v2rp-table-wrap">
      <table className="v2rp-table">
        <thead><tr>{cols.map((c) => <th key={c.label} className={c.num ? "is-num" : ""}>{c.label}</th>)}</tr></thead>
        <tbody>{shown.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c.label} className={c.num ? "is-num" : ""}>{c.render ? c.render(r) : c.num ? money(r[c.key]) : r[c.key] ?? "—"}</td>)}</tr>)}</tbody>
        {foot && <tfoot><tr>{cols.map((c, i) => <td key={c.label} className={c.num ? "is-num" : ""}>{i === 0 ? "Total" : c.num && foot[c.key] != null ? money(foot[c.key]) : ""}</td>)}</tr></tfoot>}
      </table>
      {max && rows.length > max && <p className="v2rp-more">Showing {max} of {rows.length}. Export CSV for all rows.</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BUSINESS REPORT (/dashboard/reports)
// ══════════════════════════════════════════════════════════════════════════════
export function V2BusinessReport() {
  usePageTitle("Reports");
  const p = usePeriod({ initial: "month" });
  const [kind, setKind] = useState("business");
  const q = p.mode === "month" ? `type=monthly&year=${p.start.getFullYear()}&month=${p.start.getMonth() + 1}`
    : p.mode === "quarter" ? `type=quarterly&year=${p.start.getFullYear()}&quarter=${Math.floor(p.start.getMonth() / 3) + 1}`
    : `type=yearly&year=${p.start.getFullYear()}`;
  const { data, error, loading } = useReport(`/api/reports/${kind}?${q}`);
  return (
    <div className="v2p v2rp">
      <PageHead eyebrow="Reports" title="Business report" sub="How the period went — sales, collections, clients and stock."
        actions={<button type="button" className="v2-btn" onClick={printReport}><Glyph name="invoice" size={16} /> Print</button>} />
      <div className="v2i-toolbar v2rp-toolbar">
        <Segmented ariaLabel="Report" value={kind} onChange={setKind} options={[{ value: "business", label: "Business" }, { value: "stock", label: "Inventory" }]} />
        <PeriodPicker p={p} />
      </div>
      <div className="v2rp-print-title"><strong>{kind === "business" ? "Business report" : "Inventory report"}</strong><span>{p.label}</span></div>
      {error ? <Failed msg={error} /> : kind === "business" ? <Business d={data} loading={loading} label={p.label} /> : <Inventory d={data} loading={loading} />}
    </div>
  );
}

function Business({ d, loading, label }) {
  if (!d) return <div className="v2rp-stack"><Skel h={200} r={28} /><div className="v2rp-grid2"><Skel h={260} r={26} /><Skel h={260} r={26} /></div></div>;
  const f = d.financials || {}, s = d.sales || {}, cf = d.cashFlow || {}, r = d.ratios || {}, ag = d.paymentAging?.totals || {};
  const profit = f.profit || 0;
  const split = [{ k: "Paid", v: s.paidInvoices || 0, c: SERIES.c }, { k: "Part-paid", v: s.partialInvoices || 0, c: "#eda100" }, { k: "Unpaid", v: s.unpaidInvoices || 0, c: SERIES.b }];
  const overdue = Object.values(d.paymentAging?.aging || {}).flat().sort((x, y) => y.daysOverdue - x.daysOverdue).slice(0, 5);
  // Server bug guard: getSalesSummary / getCashFlowAnalysis / getBusinessRatios read range.startDate/endDate,
  // which getDateRange never sets, so they come back empty while billing is non-zero. Don't show contradictions.
  const salesOk = !((f.revenue || 0) > 0 && !s.totalInvoices);
  const na = (v) => (salesOk ? v : "—");
  const naNote = (v) => (salesOk ? v : "Unavailable for now");
  return (
    <div className={`v2rp-stack${loading ? " is-refreshing" : ""}`}>
      <section className="v2-card v2rp-hero">
        <div className="v2rp-hero-main">
          <span className="v2rp-kicker">Billed in {label}</span>
          <strong className="v2rp-big">{inr(f.revenue)}</strong>
          <span className="v2rp-hero-sub">{salesOk ? `${s.totalInvoices || 0} invoices · ` : ""}incl. {inr(f.gstCollected)} GST</span>
        </div>
        <div className="v2rp-hero-side">
          <div><span>Expenses</span><b>{inr(f.expenses)}</b></div>
          <div><span>Billed − expenses</span><b className={profit < 0 ? "is-bad" : "is-good"}>{profit < 0 ? "−" : ""}{inr(Math.abs(profit))}</b></div>
          <div><span>Margin</span><b>{f.profitMargin || 0}%</b></div>
        </div>
        {salesOk && <div className="v2rp-hero-split">
          <div className="v2rp-split-bar">{(s.totalInvoices ? split : [{ v: 1, c: "rgba(120,130,150,.15)" }]).map((x, i) => x.v > 0 && <span key={i} style={{ flexGrow: x.v, background: x.c }} />)}</div>
          <div className="v2rp-legend">{split.map((x) => <span key={x.k} className="v2rp-legend-item"><i style={{ background: x.c }} />{x.k} <b>{x.v}</b></span>)}</div>
        </div>}
      </section>
      <div className="v2k-stats">
        <Stat label="Collection efficiency" value={na(`${r.collectionEfficiency || 0}%`)} note={naNote("Collected ÷ billed")} />
        <Stat label="Days to get paid" value={na(r.dso ? `${r.dso} days` : "—")} note={naNote("Average (DSO)")} />
        <Stat label="Average invoice" value={na(inr(s.averageOrderValue))} note={naNote(`${s.totalInvoices || 0} invoices`)} />
        <Stat label="Still to collect" value={na(inr(cf.pendingCollections))} note={naNote("From this period's invoices")} />
      </div>
      <div className="v2rp-grid2">
        <Panel title="Money owed to you" sub="All open invoices, by age">
          <AgingBar buckets={[{ label: "0–30 days", value: ag.current || 0 }, { label: "31–60", value: ag.days30 || 0 }, { label: "61–90", value: ag.days60 || 0 }, { label: "90+ days", value: ag.days90Plus || 0 }]} />
          {overdue.length > 0 && <div className="v2rp-mini">
            <span className="v2-label">Oldest open</span>
            {overdue.map((o) => <div key={o.invoiceNumber} className="v2rp-mini-row"><span><b>{o.clientName}</b> · {o.invoiceNumber}</span><span className="v2rp-days">{o.daysOverdue}d</span><b>{inr(o.amountDue)}</b></div>)}
          </div>}
        </Panel>
        <Panel title="Top clients" sub={`By billing in ${label}`}>
          <BarList rows={d.clients?.top} label={(c) => c.clientName || "—"} value={(c) => c.totalRevenue} sub={(c) => `${c.invoiceCount} invoice${c.invoiceCount === 1 ? "" : "s"}`} />
        </Panel>
        <Panel title="Best-selling products" sub="By quantity">
          <BarList rows={d.products?.top} label={(x) => x._id} value={(x) => x.totalQty} fmt={(v) => `${v} sold`} sub={(x) => inr(x.totalRevenue)} color={SERIES.c} />
        </Panel>
        <Panel title="Where the money went" sub="Expenses by category">
          <BarList rows={d.expenseBreakdown} label={(x) => x._id || "Uncategorised"} value={(x) => x.total} sub={(x) => `${x.count} entr${x.count === 1 ? "y" : "ies"}`} color={SERIES.b} empty="No expenses recorded." />
        </Panel>
        <Panel title="Watch list" sub="Clients with overdue money">
          {d.clients?.highRisk?.length ? d.clients.highRisk.map((c, i) => (
            <div key={i} className="v2rp-mini-row"><span><b>{c.clientName}</b> · {c.overdueInvoices} overdue</span><b className="is-bad">{inr(c.overdueAmount)}</b></div>))
            : <Empty glyph="clients">No overdue clients. Lovely.</Empty>}
        </Panel>
        <Panel title="Most reliable" sub="Highest billing, fully paid">
          {d.clients?.best?.length ? d.clients.best.map((c, i) => (
            <div key={i} className="v2rp-mini-row"><span><b>{c.clientName}</b> · {c.totalInvoices} invoices</span><b className="is-good">{inr(c.totalRevenue)}</b></div>))
            : <Empty glyph="clients">No fully-paid clients yet.</Empty>}
        </Panel>
      </div>
      <p className="v2rp-foot">Billed figures include GST. Ageing covers every open invoice, not just this period.</p>
    </div>
  );
}

function Inventory({ d, loading }) {
  if (!d) return <div className="v2rp-stack"><div className="v2rp-grid2"><Skel h={260} r={26} /><Skel h={260} r={26} /></div></div>;
  const s = d.summary || {}, m = d.movement || {};
  return (
    <div className={`v2rp-stack${loading ? " is-refreshing" : ""}`}>
      <div className="v2k-stats">
        <Stat label="Products" value={s.totalProducts || 0} note={`${s.outOfStock || 0} out · ${s.lowStock || 0} low`} />
        <Stat label="Stock at selling price" value={inr(s.totalStockValue)} note="Current" />
        <Stat label="Stock at cost" value={s.totalPurchaseValue ? inr(s.totalPurchaseValue) : "—"} note={s.totalPurchaseValue ? "Current" : "No cost prices set"} />
        <Stat label="Movement" value={`${m.netChange > 0 ? "+" : ""}${m.netChange || 0}`} note={`+${m.totalAdded || 0} in · −${m.totalRemoved || 0} out`} />
      </div>
      <div className="v2rp-grid2">
        <Panel title="Stock value by category" sub="At selling price">
          <BarList rows={d.categoryWise} label={(c) => c.category} value={(c) => c.stockValue} sub={(c) => `${c.products} products · ${c.totalStock} units`} />
        </Panel>
        <Panel title="Fastest movers" sub="Units sold in the period">
          <BarList rows={(d.productMovement || []).filter((x) => x.sold > 0).slice(0, 8)} label={(x) => x.name} value={(x) => x.sold} fmt={(v) => `${v} sold`} sub={(x) => `${x.opening} → ${x.closing} in stock`} color={SERIES.c} empty="No sales movement recorded." />
        </Panel>
        <Panel title="Out of stock" sub={`${d.outOfStock?.length || 0} products`}>
          {d.outOfStock?.length ? <div className="v2rp-chips">{d.outOfStock.slice(0, 24).map((x) => <span key={x.name}>{x.name}</span>)}</div> : <Empty glyph="box">Everything is in stock.</Empty>}
        </Panel>
        <Panel title="Not selling" sub="No sales in the period, by capital tied up">
          <BarList rows={d.deadStock} label={(x) => x.name} value={(x) => x.stockValue} sub={(x) => `${x.currentStock} units`} color="#8a94a6" empty="Everything sold at least once." />
        </Panel>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FINANCIAL STATEMENTS (/dashboard/financial-reports)
// ══════════════════════════════════════════════════════════════════════════════
const FIN_TABS = [
  { value: "summary", label: "Overview" }, { value: "pl", label: "Profit & loss" }, { value: "balance", label: "Balance sheet" },
  { value: "cashflow", label: "Cash flow" }, { value: "ar", label: "Receivables" }, { value: "ap", label: "Payables" }, { value: "stock", label: "Stock value" },
];
export function V2Financials() {
  usePageTitle("Financial statements");
  const [fyList, setFyList] = useState(null);
  const [fy, setFy] = useState("");
  const [tab, setTab] = useState("summary");
  const [opening, setOpening] = useState({ stock: "", capital: "" });
  const [applied, setApplied] = useState({ stock: 0, capital: 0 });
  useEffect(() => {
    getJSON("/api/financials/fy-list").then((d) => { const l = d.fyList || []; setFyList(l); setFy((l.find((x) => x.current) || l[0])?.fy || ""); })
      .catch(() => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; setFyList([{ fy: String(y), label: `FY ${y}-${String(y + 1).slice(2)}`, current: true }]); setFy(String(y)); });
  }, []);
  const path = !fy ? null : {
    summary: `/api/financials/summary?fy=${fy}`,
    pl: `/api/financials/profit-loss?fy=${fy}&openingStock=${applied.stock}`,
    balance: `/api/financials/balance-sheet?fy=${fy}&openingStock=${applied.stock}&openingCapital=${applied.capital}`,
    cashflow: `/api/financials/cash-flow?fy=${fy}`,
    ar: "/api/financials/accounts-receivable", ap: "/api/financials/accounts-payable", stock: "/api/financials/stock-valuation",
  }[tab];
  const { data, error, loading } = useReport(path);
  const fyLabel = fyList?.find((x) => x.fy === fy)?.label || "";
  const asOf = ["ar", "ap", "stock"].includes(tab);
  const show = data && !loading ? data : null;
  const openingBox = (withCapital) => (
    <form className="v2-inset v2rp-opening" onSubmit={(e) => { e.preventDefault(); setApplied({ stock: parseFloat(opening.stock) || 0, capital: parseFloat(opening.capital) || 0 }); }}>
      <label><span>Opening stock</span><span className="v2-input-wrap"><span className="v2-input-prefix">₹</span><input className="v2-input" type="number" min="0" value={opening.stock} onChange={(e) => setOpening((o) => ({ ...o, stock: e.target.value }))} placeholder="0" /></span></label>
      {withCapital && <label><span>Opening capital</span><span className="v2-input-wrap"><span className="v2-input-prefix">₹</span><input className="v2-input" type="number" value={opening.capital} onChange={(e) => setOpening((o) => ({ ...o, capital: e.target.value }))} placeholder="0" /></span></label>}
      <button type="submit" className="v2-btn v2-btn--sm">Apply</button>
      <span className="v2rp-opening-note">Not stored — used only for this view.</span>
    </form>
  );
  return (
    <div className="v2p v2rp">
      <PageHead eyebrow="Reports" title="Financial statements" sub="Profit, position and cash for the financial year."
        actions={<>
          {fyList && <MenuButton className="v2-btn" label="Financial year" width={200} items={fyList.map((x) => ({ label: x.label, active: x.fy === fy, onSelect: () => setFy(x.fy) }))}>
            <Glyph name="calendar" size={16} /> {fyLabel || "FY"} <Glyph name="chevronDown" size={14} stroke={2} /></MenuButton>}
          <button type="button" className="v2-btn" onClick={printReport}><Glyph name="invoice" size={16} /> Print</button>
        </>} />
      <div className="v2rp-tabs" role="tablist">
        {FIN_TABS.map((t) => <button key={t.value} type="button" role="tab" aria-selected={tab === t.value} className={`v2-chip-btn v2i-preset${tab === t.value ? " is-on" : ""}`} onClick={() => setTab(t.value)}>{t.label}</button>)}
      </div>
      <div className="v2rp-print-title"><strong>{FIN_TABS.find((t) => t.value === tab).label}</strong><span>{asOf ? `As of ${fmtDate(new Date())}` : fyLabel}</span></div>
      {asOf && <p className="v2rp-asof"><Glyph name="clock" size={14} /> As of today — not limited to {fyLabel}.</p>}
      {error ? <Failed msg={error} /> : !show ? <div className="v2rp-stack"><Skel h={140} r={26} /><Skel h={320} r={26} /></div> : (
        <div key={tab} className="v2rp-stack v2rp-enter">
          {tab === "summary" && <FinSummary d={show} />}
          {tab === "pl" && <><PL d={show} />{openingBox(false)}</>}
          {tab === "balance" && <><Balance d={show} />{openingBox(true)}</>}
          {tab === "cashflow" && <CashFlow d={show} />}
          {tab === "ar" && <Aging d={show} who="clients" name={(c) => c.clientName} items={(c) => c.invoices} ref_={(x) => x.invoiceNumber} date={(x) => x.invoiceDate} title="Owed to you" />}
          {tab === "ap" && <Aging d={show} who="suppliers" name={(c) => c.supplierName} items={(c) => c.purchases} ref_={(x) => x.purchaseNumber} date={(x) => x.purchaseDate} title="You owe" />}
          {tab === "stock" && <StockValue d={show} />}
        </div>)}
    </div>
  );
}

function FinSummary({ d }) {
  const k = d.kpis || {};
  const loss = k.netProfit < 0;
  return <>
    <section className="v2-card v2rp-hero">
      <div className="v2rp-hero-main">
        <span className="v2rp-kicker">{loss ? "Net loss" : "Net profit"} · {d.fyLabel}</span>
        <strong className={`v2rp-big${loss ? " is-bad" : ""}`}>{inr(Math.abs(k.netProfit))}</strong>
        <span className="v2rp-hero-sub">{k.profitMargin || 0}% margin on {inr(k.totalRevenue)} revenue (before GST)</span>
      </div>
      <div className="v2rp-hero-side">
        <div><span>Revenue</span><b>{inr(k.totalRevenue)}</b></div>
        <div><span>Expenses</span><b>{inr(k.totalExpenses)}</b></div>
        <div><span>Received</span><b>{inr(k.amountReceived)}</b></div>
      </div>
    </section>
    <div className="v2k-stats">
      <Stat label="Clients owe you" value={inr(k.arOutstanding)} note="Open invoices" />
      <Stat label="You owe suppliers" value={inr(k.apOutstanding)} note="Open purchases" />
      <Stat label="Invoices" value={k.invoiceCount || 0} note="In the year" />
      <Stat label="Purchases" value={k.purchaseCount || 0} note="In the year" />
    </div>
    <Panel title="Month by month" sub="Revenue before GST vs. purchases, expenses, bills and payroll">
      {d.monthly?.some((m) => m.revenue || m.expenses)
        ? <MonthlyChart data={d.monthly} extra={(m) => <span className="v2rp-tip-total">Profit<b className={m.profit < 0 ? "is-bad" : "is-good"}>{m.profit < 0 ? "−" : ""}{inr(Math.abs(m.profit))}</b></span>} />
        : <Empty>No activity recorded in this year.</Empty>}
    </Panel>
  </>;
}

function PL({ d }) {
  const r = d.revenue || {}, c = d.cogs || {}, o = d.operatingExpenses || {};
  const grossNeg = d.grossLoss > 0, netNeg = d.netLoss > 0;
  const stockSwing = !c.openingStock && (c.closingStock || 0) > (c.purchases || 0);
  return (<>
    {stockSwing && <div className="v2-banner v2-banner--warn"><Glyph name="alert" size={16} /> No opening stock is entered, so {inr((c.closingStock || 0) - (c.purchases || 0))} of closing stock is counted as profit. Enter last year's closing stock as opening stock below for a true figure.</div>}
    <section className="v2-card v2rp-statement">
      <header className="v2rp-st-head"><span>Profit &amp; loss</span><strong>{d.fyLabel}</strong></header>
      <Line label="Revenue" kind="section" />
      <Line label="Sales (taxable value)" value={r.taxable} level={1} note={`${r.invoiceCount} invoices`} />
      <Line label="GST collected" value={r.gstOutput} level={1} note="Not income — owed to government" kind="muted" />
      <Line label="Cost of goods sold" kind="section" />
      <Line label="Opening stock" value={c.openingStock} level={1} />
      <Line label="Purchases" value={c.purchases} level={1} />
      {mapLines(c.purchasesByCategory, 2)}
      <Line label="Closing stock" value={c.closingStock} level={1} neg />
      <Line label="Cost of goods sold" value={c.total} kind="sub" />
      <Line label={grossNeg ? "Gross loss" : "Gross profit"} value={grossNeg ? d.grossLoss : d.grossProfit} kind={grossNeg ? "total-bad" : "total"} />
      <Line label="Operating expenses" kind="section" />
      <Line label="Expenses" value={o.expenses?.total} level={1} />{mapLines(o.expenses?.byHead, 2)}
      <Line label="Bills" value={o.bills?.total} level={1} />{mapLines(o.bills?.byCategory, 2)}
      <Line label="Payroll" value={o.payroll?.total} level={1} note={o.payroll?.pending ? `${inr(o.payroll.pending)} unpaid` : undefined} />
      <Line label="Sales commission" value={o.commissions?.total} level={1} />
      <Line label="Total operating expenses" value={o.total} kind="sub" />
      <Line label={netNeg ? "Net loss" : "Net profit"} value={netNeg ? d.netLoss : d.netProfit} kind={netNeg ? "grand-bad" : "grand"} />
      <div className="v2rp-st-note"><Glyph name="gst" size={15} /> GST: {money(d.gst?.output)} collected − {money(d.gst?.input)} input credit = <b>{money(d.gst?.netPayable)}</b> net payable</div>
    </section>
  </>);
}

function Balance({ d }) {
  const a = d.assets?.current || {}, l = d.liabilities?.current || {}, c = d.capital || {};
  return <>
    <div className={`v2rp-balanced ${d.balanced ? "is-good" : "is-warn"}`}>
      <Glyph name={d.balanced ? "check" : "alert"} size={16} stroke={2.2} />
      {d.balanced ? "Balanced — assets equal liabilities plus capital." : `Not balanced — difference of ${money(Math.abs((d.totalAssets || 0) - (d.totalLiabilitiesAndCapital || 0)))}. Enter opening capital to reconcile.`}
    </div>
    <div className="v2rp-grid2 v2rp-grid2--tight">
      <section className="v2-card v2rp-statement">
        <header className="v2rp-st-head"><span>Assets</span><strong>{d.fyLabel}</strong></header>
        <Line label="Current assets" kind="section" />
        <Line label="Closing stock" value={a.closingStock} level={1} />
        <Line label="Trade receivables" value={a.tradeReceivables} level={1} note="Clients owe you" />
        <Line label="TDS receivable" value={a.tdsReceivable} level={1} />
        <Line label="GST input credit" value={a.gstCreditAsset} level={1} />
        <Line label="Total assets" value={d.totalAssets} kind="grand" />
      </section>
      <section className="v2-card v2rp-statement">
        <header className="v2rp-st-head"><span>Liabilities &amp; capital</span><strong>{d.fyLabel}</strong></header>
        <Line label="Current liabilities" kind="section" />
        <Line label="Trade payables" value={l.tradePayables} level={1} note="You owe suppliers" />
        <Line label="Bills payable" value={l.billsPayable} level={1} />
        <Line label="Payroll owed" value={l.accruedPayroll} level={1} />
        <Line label="GST payable" value={l.gstLiability} level={1} />
        <Line label="Total liabilities" value={d.liabilities?.total} kind="sub" />
        <Line label="Capital" kind="section" />
        <Line label="Opening capital" value={c.opening} level={1} />
        {c.netLoss > 0 ? <Line label="Less: net loss" value={c.netLoss} level={1} neg /> : <Line label="Add: net profit" value={c.netProfit} level={1} />}
        <Line label="Closing capital" value={c.closing} kind="sub" />
        <Line label="Total liabilities & capital" value={d.totalLiabilitiesAndCapital} kind="grand" />
      </section>
    </div>
  </>;
}

function CashFlow({ d }) {
  const op = d.operating || {}, i = op.inflows || {}, o = op.outflows || {};
  const neg = (d.netCashFlow || 0) < 0;
  const max = Math.max(i.total || 0, o.total || 0, 1);
  return <>
    <section className="v2-card v2rp-hero v2rp-hero--cash">
      <div className="v2rp-hero-main">
        <span className="v2rp-kicker">Net cash {neg ? "out" : "in"} · {d.fyLabel}</span>
        <strong className={`v2rp-big${neg ? " is-bad" : ""}`}>{neg ? "−" : "+"}{inr(Math.abs(d.netCashFlow))}</strong>
      </div>
      <div className="v2rp-flow">
        <div><span>In</span><div className="v2rp-flow-track"><span style={{ width: `${((i.total || 0) / max) * 100}%`, background: SERIES.c }} /></div><b>{inr(i.total)}</b></div>
        <div><span>Out</span><div className="v2rp-flow-track"><span style={{ width: `${((o.total || 0) / max) * 100}%`, background: SERIES.b }} /></div><b>{inr(o.total)}</b></div>
      </div>
    </section>
    <section className="v2-card v2rp-statement">
      <header className="v2rp-st-head"><span>Cash flow</span><strong>{d.fyLabel}</strong></header>
      <Line label="Operating activities" kind="section" />
      <Line label="Received from clients" value={i.clientReceipts} level={1} />
      <Line label="Paid to suppliers" value={o.supplierPayments} level={1} neg />
      <Line label="Bills paid" value={o.billPayments} level={1} neg />
      <Line label="Salaries paid" value={o.payrollPayments} level={1} neg />
      <Line label="Expenses paid" value={o.expensePayments} level={1} neg />
      <Line label="Commission paid" value={o.commissionPayments} level={1} neg />
      <Line label="Net from operations" value={Math.abs(op.net || 0)} neg={(op.net || 0) < 0} kind="sub" />
      <Line label="Investing activities" value={d.investing?.net} kind="section" note={d.investing?.note} />
      <Line label="Financing activities" value={d.financing?.net} kind="section" note={d.financing?.note} />
      <Line label="Net change in cash" value={Math.abs(d.netCashFlow || 0)} neg={neg} kind={neg ? "grand-bad" : "grand"} />
    </section>
  </>;
}

function Aging({ d, who, name, items, ref_, date, title }) {
  const [open, setOpen] = useState(null);
  const a = d.aging || {};
  const list = d[who] || [];
  return <>
    <section className="v2-card v2rp-hero">
      <div className="v2rp-hero-main"><span className="v2rp-kicker">{title}</span><strong className="v2rp-big">{inr(d.totalOutstanding)}</strong><span className="v2rp-hero-sub">{list.length} {who} with open balances</span></div>
      <div className="v2rp-hero-wide"><AgingBar total={d.totalOutstanding} buckets={[{ label: "0–30 days", value: a.current || 0 }, { label: "31–60", value: a.days30_60 || 0 }, { label: "61–90", value: a.days60_90 || 0 }, { label: "90+ days", value: a.days90plus || 0 }]} /></div>
    </section>
    <Panel title={who === "clients" ? "By client" : "By supplier"} sub="Tap to see the open items">
      {!list.length ? <Empty glyph={who === "clients" ? "clients" : "truck"}>Nothing outstanding.</Empty> : <div className="v2rp-acc">
        {list.map((c, ci) => {
          const id = c.clientId || c.supplierId || ci; const isOpen = open === id; const its = items(c) || [];
          const oldest = Math.max(...its.map((x) => x.daysOld), 0);
          return (
            <div key={id} className={`v2rp-acc-item${isOpen ? " is-open" : ""}`}>
              <button type="button" className="v2rp-acc-head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : id)}>
                <span className="v2-row-main"><span className="v2-row-title">{name(c)}</span><span className="v2-row-sub">{its.length} open · oldest {oldest} days</span></span>
                <span className={`v2rp-days${oldest > 90 ? " is-bad" : oldest > 30 ? " is-warn" : ""}`}>{oldest}d</span>
                <b>{inr(c.total)}</b><Glyph name="chevronDown" size={16} stroke={2} className="v2rp-acc-chev" />
              </button>
              <div className="v2rp-acc-body"><div>{its.sort((x, y) => y.daysOld - x.daysOld).map((x, i) => (
                <div key={i} className="v2rp-mini-row"><span><b>{ref_(x)}</b> · {fmtDate(date(x))}</span><span className="v2rp-days">{x.daysOld}d</span><b>{money(x.outstanding)}</b></div>))}</div></div>
            </div>);
        })}
      </div>}
    </Panel>
  </>;
}

function StockValue({ d }) {
  const s = d.summary || {};
  const [view, setView] = useState("value");
  const rows = (d.products || []).filter((p) => view === "value" ? p.currentStock > 0 : view === "dead" ? p.isDead && p.stockValue > 0 : p.isLow || p.isOutOfStock);
  return <>
    <div className="v2k-stats">
      <Stat label="Stock at cost" value={inr(s.totalStockValue)} note={`${s.totalProducts || 0} products`} />
      <Stat label="Stock at selling price" value={inr(s.totalPotentialRevenue)} note="If all sold" />
      <Stat label="Slow-moving stock" value={inr(s.deadStockValue)} note={`${s.deadStockCount || 0} products, no movement in 90 days`} />
      <Stat label="Low or out" value={(s.lowStockCount || 0) + (s.outOfStockCount || 0)} note={`${s.lowStockCount || 0} low · ${s.outOfStockCount || 0} out`} />
    </div>
    {!s.totalStockValue && s.totalPotentialRevenue > 0 && <div className="v2-banner v2-banner--warn"><Glyph name="alert" size={16} /> No purchase prices are set, so stock at cost shows ₹0. Add cost prices in Stock to value inventory properly.</div>}
    <Panel title="Products" action={<Segmented ariaLabel="Filter" value={view} onChange={setView} options={[{ value: "value", label: "In stock" }, { value: "dead", label: "Slow" }, { value: "low", label: "Low / out" }]} />}>
      <Table max={40} rows={rows} empty="No products here." cols={[
        { label: "Product", key: "name", render: (p) => <span className="v2rp-cell-main">{p.name}{p.isDead && p.stockValue > 0 && <em>slow</em>}{p.isOutOfStock && <em className="is-bad">out</em>}{p.isLow && <em className="is-warn">low</em>}</span> },
        { label: "Qty", key: "currentStock", render: (p) => p.currentStock, num: true },
        { label: "Cost", key: "purchasePrice", num: true },
        { label: "Value at cost", key: "stockValue", num: true },
        { label: "Margin", key: "margin", render: (p) => `${p.margin}%`, num: true },
      ]} />
    </Panel>
  </>;
}

// ══════════════════════════════════════════════════════════════════════════════
// GST REPORTS (/dashboard/gst-reports)
// ══════════════════════════════════════════════════════════════════════════════
export function V2GSTReports() {
  usePageTitle("GST reports");
  const { user } = useContext(userContext);
  const p = usePeriod({ fy: true, initial: "month" });
  const [tab, setTab] = useState("3b");
  const [source, setSource] = useState("all");
  const range = p.mode === "year" ? `fy=${p.start.getFullYear()}` : `from=${ymd(p.start)}&to=${ymd(p.end)}`;
  const path = { "3b": `/api/gst-reports/gstr3b?${range}`, "1": `/api/gst-reports/gstr1?${range}`, hsn: `/api/gst-reports/hsn-summary?${range}`,
    itc: p.mode === "month" ? `/api/gst-reports/input-credit?month=${p.start.getMonth() + 1}&year=${p.start.getFullYear()}&source=${source}` : `/api/gst-reports/input-credit/yearly?year=${p.start.getFullYear()}` }[tab];
  const { data, error, loading } = useReport(path);
  const slug = `${p.label.replace(/[^\w]+/g, "-")}`;
  const show = data && !loading ? data : null;
  return (
    <div className="v2p v2rp">
      <PageHead eyebrow="Reports" title="GST reports" sub={`Return-ready summaries${user?.companyGSTIN ? ` for ${user.companyGSTIN}` : ""}. Check against your books before filing.`}
        actions={<button type="button" className="v2-btn" onClick={printReport}><Glyph name="invoice" size={16} /> Print</button>} />
      <div className="v2i-toolbar v2rp-toolbar">
        <Segmented ariaLabel="Return" value={tab} onChange={setTab} options={[{ value: "3b", label: "GSTR-3B" }, { value: "1", label: "GSTR-1" }, { value: "hsn", label: "HSN" }, { value: "itc", label: "Input credit" }]} />
        <PeriodPicker p={p} fy />
      </div>
      <div className="v2rp-print-title"><strong>{{ "3b": "GSTR-3B summary", 1: "GSTR-1 outward supplies", hsn: "HSN summary", itc: "Input tax credit" }[tab]}</strong><span>{p.label}</span></div>
      {error ? <Failed msg={error} /> : !show ? <div className="v2rp-stack"><Skel h={180} r={28} /><Skel h={280} r={26} /></div> : (
        <div key={tab + p.mode} className="v2rp-stack v2rp-enter">
          {tab === "3b" && <G3B d={show} label={p.label} />}
          {tab === "1" && <G1 d={show} slug={slug} />}
          {tab === "hsn" && <HSN d={show} slug={slug} />}
          {tab === "itc" && (p.mode === "month" ? <ITC d={show} slug={slug} source={source} setSource={setSource} /> : <ITCYear d={show} />)}
        </div>)}
    </div>
  );
}

function G3B({ d, label }) {
  const o = d.outward || {}, i = d.itc || {}, n = d.netPayable || {}, x = d.itcExcess || {};
  const cols = ["cgst", "sgst", "igst"];
  const pay = Math.max(n.total || 0, 0);
  const row = (name, src, kind, sign) => (
    <div className={`v2rp-3b-row${kind ? ` v2rp-3b-row--${kind}` : ""}`}>
      <span>{name}</span>{cols.map((c) => <b key={c}>{sign}{money(Math.max(src[c] || 0, 0))}</b>)}<b>{sign}{money(Math.max(src.total ?? src.totalTax ?? 0, 0))}</b>
    </div>);
  return <>
    <section className="v2-card v2rp-hero v2rp-hero--gst">
      <div className="v2rp-hero-main">
        <span className="v2rp-kicker">Net GST to pay · {label}</span>
        <strong className="v2rp-big">{inr(pay, { decimals: 2 })}</strong>
        <span className="v2rp-hero-sub">{x.total > 0 ? `${money(x.total)} input credit carries forward` : "After setting off input credit"}</span>
      </div>
      <div className="v2rp-eq">
        <div><span>Output tax</span><b>{inr(o.totalTax)}</b></div><i>−</i>
        <div><span>Input credit</span><b>{inr(i.totalTax)}</b></div><i>=</i>
        <div className="is-hi"><span>Payable</span><b>{inr(pay)}</b></div>
      </div>
    </section>
    <Panel title="Tax by head" sub={[[d.invoiceCount, "invoice"], [d.purchaseCount, "purchase"], [d.billCount, "bill"]].map(([n, w]) => `${n} ${w}${n === 1 ? "" : "s"}`).join(" · ")}>
      <div className="v2rp-3b">
        <div className="v2rp-3b-row v2rp-3b-row--head"><span /><b>CGST</b><b>SGST</b><b>IGST</b><b>Total</b></div>
        {row("3.1 Outward tax", o)}
        {row("4 Eligible input credit", i, "minus", "− ")}
        {row("Net payable", n, "total")}
        {x.total > 0 && row("Credit carried forward", x, "muted")}
      </div>
      <div className="v2rp-split3">
        <div><span>Taxable outward value</span><b>{money(o.taxableValue)}</b></div>
        <div><span>Credit from purchases</span><b>{money((i.purchaseCgst || 0) + (i.purchaseSgst || 0) + (i.purchaseIgst || 0))}</b></div>
        <div><span>Credit from bills</span><b>{money((i.billCgst || 0) + (i.billSgst || 0) + (i.billIgst || 0))}</b></div>
      </div>
      {d.crossUtilization?.igstUsedForCgstSgst > 0 && <p className="v2rp-foot">{money(d.crossUtilization.igstUsedForCgstSgst)} of IGST credit was used against CGST/SGST.</p>}
    </Panel>
  </>;
}

function G1({ d, slug }) {
  const s = d.summary || {};
  const [view, setView] = useState("b2b");
  const exp = () => view === "b2b"
    ? downloadCSV(`GSTR1-B2B-${slug}.csv`, [{ label: "Invoice", get: "invoiceNumber" }, { label: "Date", get: (r) => ymd(new Date(r.invoiceDate)) }, { label: "Client", get: "clientName" }, { label: "GSTIN", get: "clientGSTIN" }, { label: "Place of supply", get: "placeOfSupply" }, { label: "Taxable", get: "taxableValue" }, { label: "CGST", get: "cgst" }, { label: "SGST", get: "sgst" }, { label: "IGST", get: "igst" }, { label: "Invoice value", get: "grandTotal" }], d.b2b || [])
    : downloadCSV(`GSTR1-B2C-${slug}.csv`, [{ label: "Rate %", get: "gstRate" }, { label: "Taxable", get: "taxableValue" }, { label: "CGST", get: "cgst" }, { label: "SGST", get: "sgst" }, { label: "IGST", get: "igst" }, { label: "Total tax", get: "totalTax" }], d.b2c || []);
  return <>
    <div className="v2k-stats">
      <Stat label="Taxable value" value={inr(s.taxableValue)} note={`${s.totalInvoices || 0} invoices`} />
      <Stat label="Tax charged" value={inr(s.totalTax)} note={`C ${inrCompact(s.totalCgst)} · S ${inrCompact(s.totalSgst)} · I ${inrCompact(s.totalIgst)}`} />
      <Stat label="B2B invoices" value={s.b2bCount || 0} note="Registered buyers" />
      <Stat label="B2C invoices" value={s.b2cCount || 0} note="Unregistered buyers" />
    </div>
    <Panel title={view === "b2b" ? "B2B — invoice-wise" : "B2C — rate-wise"}
      action={<div className="v2rp-actions"><Segmented ariaLabel="Section" value={view} onChange={setView} options={[{ value: "b2b", label: `B2B · ${s.b2bCount || 0}` }, { value: "b2c", label: "B2C" }]} />
        <button type="button" className="v2-btn v2-btn--sm" onClick={exp}><Glyph name="download" size={15} /> CSV</button></div>}>
      {view === "b2b" ? <Table rows={d.b2b} max={60} cols={[
        { label: "Invoice", key: "invoiceNumber", render: (r) => <span className="v2rp-cell-main">{r.invoiceNumber}<small>{fmtDate(r.invoiceDate)}</small></span> },
        { label: "Buyer", key: "clientName", render: (r) => <span className="v2rp-cell-main">{r.clientName}<small className="v2rp-mono">{r.clientGSTIN}</small></span> },
        { label: "Taxable", key: "taxableValue", num: true }, { label: "Tax", key: "totalTax", num: true }, { label: "Invoice value", key: "grandTotal", num: true },
      ]} foot={{ taxableValue: (d.b2b || []).reduce((a, r) => a + r.taxableValue, 0), totalTax: (d.b2b || []).reduce((a, r) => a + r.totalTax, 0), grandTotal: (d.b2b || []).reduce((a, r) => a + r.grandTotal, 0) }} />
        : <Table rows={d.b2c} cols={[
          { label: "GST rate", key: "gstRate", render: (r) => `${r.gstRate}%` }, { label: "Taxable", key: "taxableValue", num: true },
          { label: "CGST", key: "cgst", num: true }, { label: "SGST", key: "sgst", num: true }, { label: "IGST", key: "igst", num: true }, { label: "Total tax", key: "totalTax", num: true },
        ]} foot={["taxableValue", "cgst", "sgst", "igst", "totalTax"].reduce((o, k) => ({ ...o, [k]: (d.b2c || []).reduce((a, r) => a + r[k], 0) }), {})} />}
    </Panel>
  </>;
}

function HSN({ d, slug }) {
  const t = d.totals || {};
  const rows = d.hsnSummary || [];
  const missing = rows.filter((r) => !r.hsnCode || /^(n\/?a|-|—)?$/i.test(String(r.hsnCode).trim()));
  return <>
    <div className="v2k-stats">
      <Stat label="HSN codes" value={rows.length} note={missing.length ? `${missing.length} without a code` : "All coded"} tone={missing.length ? "warn" : undefined} />
      <Stat label="Taxable value" value={inr(t.taxableValue)} note="All codes" />
      <Stat label="Total tax" value={inr(t.totalTax)} note={`IGST ${inrCompact(t.igst)}`} />
      <Stat label="CGST + SGST" value={inr((t.cgst || 0) + (t.sgst || 0))} note="Intra-state" />
    </div>
    <Panel title="HSN-wise summary" action={<button type="button" className="v2-btn v2-btn--sm" onClick={() => downloadCSV(`HSN-${slug}.csv`, [{ label: "HSN", get: "hsnCode" }, { label: "Description", get: "description" }, { label: "UQC", get: "uom" }, { label: "Quantity", get: "totalQty" }, { label: "Taxable", get: "taxableValue" }, { label: "CGST", get: "cgst" }, { label: "SGST", get: "sgst" }, { label: "IGST", get: "igst" }, { label: "Total tax", get: "totalTax" }], rows)}><Glyph name="download" size={15} /> CSV</button>}>
      <Table rows={rows} foot={t} cols={[
        { label: "HSN", key: "hsnCode", render: (r) => <span className="v2rp-cell-main"><span className="v2rp-mono">{r.hsnCode || "—"}</span><small>{r.description}</small></span> },
        { label: "Qty", key: "totalQty", render: (r) => `${r.totalQty} ${r.uom || ""}`, num: true },
        { label: "Taxable", key: "taxableValue", num: true }, { label: "CGST", key: "cgst", num: true }, { label: "SGST", key: "sgst", num: true }, { label: "IGST", key: "igst", num: true },
      ]} />
    </Panel>
  </>;
}

function ITC({ d, slug, source, setSource }) {
  const g = d.grandSummary || {}, b = d.billSummary || {}, pu = d.purchaseSummary || {};
  const rows = useMemo(() => [
    ...(d.bills || []).map((x) => ({ src: "Bill", ref: x.title, party: x.vendor, gstin: x.vendorGSTIN, date: x.billDate, cgst: x.cgst || 0, sgst: x.sgst || 0, igst: x.igst || 0, total: x.totalGST || 0 })),
    ...(d.purchases || []).map((x) => ({ src: "Purchase", ref: x.purchaseNumber, party: x.supplierId?.supplierName, gstin: x.supplierId?.gstin, date: x.purchaseDate, cgst: x.totalCgst || 0, sgst: x.totalSgst || 0, igst: x.totalIgst || 0, total: (x.totalCgst || 0) + (x.totalSgst || 0) + (x.totalIgst || 0) })),
  ].sort((x, y) => new Date(y.date) - new Date(x.date)), [d]);
  return <>
    <section className="v2-card v2rp-hero">
      <div className="v2rp-hero-main"><span className="v2rp-kicker">Input credit available</span><strong className="v2rp-big">{inr(g.total, { decimals: 2 })}</strong><span className="v2rp-hero-sub">{g.count || 0} documents</span></div>
      <div className="v2rp-hero-side">
        <div><span>From purchases</span><b>{inr(pu.total)}</b></div>
        <div><span>From bills</span><b>{inr(b.total)}</b></div>
        <div><span>CGST · SGST · IGST</span><b className="v2rp-small">{inrCompact(g.cgst)} · {inrCompact(g.sgst)} · {inrCompact(g.igst)}</b></div>
      </div>
    </section>
    <Panel title="Documents" action={<div className="v2rp-actions"><Segmented ariaLabel="Source" value={source} onChange={setSource} options={[{ value: "all", label: "All" }, { value: "purchases", label: "Purchases" }, { value: "bills", label: "Bills" }]} />
      <button type="button" className="v2-btn v2-btn--sm" onClick={() => downloadCSV(`ITC-${slug}.csv`, [{ label: "Source", get: "src" }, { label: "Reference", get: "ref" }, { label: "Supplier", get: "party" }, { label: "GSTIN", get: "gstin" }, { label: "Date", get: (r) => ymd(new Date(r.date)) }, { label: "CGST", get: "cgst" }, { label: "SGST", get: "sgst" }, { label: "IGST", get: "igst" }, { label: "Total", get: "total" }], rows)}><Glyph name="download" size={15} /> CSV</button></div>}>
      <Table rows={rows} max={60} empty="No GST-bearing purchases or bills this month." cols={[
        { label: "Document", key: "ref", render: (r) => <span className="v2rp-cell-main">{r.ref}<small>{r.src} · {fmtDate(r.date)}</small></span> },
        { label: "Supplier", key: "party", render: (r) => <span className="v2rp-cell-main">{r.party || "—"}<small className="v2rp-mono">{r.gstin || "No GSTIN"}</small></span> },
        { label: "CGST", key: "cgst", num: true }, { label: "SGST", key: "sgst", num: true }, { label: "IGST", key: "igst", num: true }, { label: "Total", key: "total", num: true },
      ]} />
      {rows.some((r) => !r.gstin) && <p className="v2rp-foot">Documents without a supplier GSTIN usually can't be claimed — check before filing.</p>}
    </Panel>
  </>;
}

function ITCYear({ d }) {
  const data = (d.monthly || []).map((m) => ({ month: MONTHS[m.month - 1].slice(0, 3), purchases: m.purchases, bills: m.bills, total: m.total }));
  const total = data.reduce((a, m) => a + m.total, 0);
  return <>
    <div className="v2-banner v2-banner--info"><Glyph name="calendar" size={16} /> Input credit is itemised by month — switch to Month for documents. Showing calendar year {d.year}.</div>
    <Panel title={`Input credit in ${d.year}`} sub={`${inr(total)} in total`}>
      {total > 0 ? <MonthlyChart data={data} a={{ key: "purchases", label: "Purchases" }} b={{ key: "bills", label: "Bills" }} extra={(m) => <span className="v2rp-tip-total">Total<b>{inr(m.total)}</b></span>} /> : <Empty glyph="gst">No input credit recorded this year.</Empty>}
    </Panel>
  </>;
}
